import { Box, Divider, Typography } from "@mui/material";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";

import styles from "../../../assets/styles/componentStyles/billingStyles/changePlan.module.scss";

import { thousandSeparator, formatDateTime } from "../../../utils/constants";
import { getAddOnAmount, getAddOnTitle, getBillingCycleName, getSubscriptionPlanAddOns, isAiCreditsAddOn, isSubscriptionOneTimeAddOn, shouldShowAddOns } from "../utils.billing";
import { CYCLE_TYPES } from "../constant.billing";
import { billingSubscriptionPlan, billingSubscriptionAddOn } from "../../../models/axiosModel";

interface PlanSummaryCardProps {
    plan: billingSubscriptionPlan | null | undefined;
    title?: string;
    addOnsLabel?: string;
    showEffectiveFromDate?: boolean;
    showPlanStartDate?: boolean;
}

const PlanSummaryCard = (props: PlanSummaryCardProps) => {

    const { plan, title = "Current Active Plan", addOnsLabel = "Active Add-ons", showEffectiveFromDate = false, showPlanStartDate = false } = props;

    // Constants
    const planName = plan?.billing_plan_name || "No Active Plan";
    const planPrice = Number(plan?.razorpay_plan_amount) || 0;
    const currencySymbol = plan?.billing_plan_currency_symbol || "₹";
    const currencyCode = plan?.billing_plan_currency || "INR";
    const billingCycle = plan?.billing_plan_cycle_period_name || CYCLE_TYPES.MONTHLY;
    const addOns = getSubscriptionPlanAddOns(plan);
    const effectiveFromDate = plan?.effective_from ? formatDateTime(plan.effective_from, "dmTexty") : null;
    const planStartDate = plan?.current_period_start ? formatDateTime(plan.current_period_start, "dmTexty") : null;

    // Functions
    const getCreditsLabel = (addOn: billingSubscriptionAddOn) => {
        if (!isAiCreditsAddOn(addOn)) return "";

        const quantity = Math.max(addOn.quantity || 1, 1);
        const perPack = Number(addOn.billing_plan_feature_limit) || 0;
        
        if (perPack <= 0) return "";

        const totalCredits = thousandSeparator(perPack * quantity);

        return ` (${totalCredits} credits)`;
    };

    return (
        <Box className={styles.planSummaryCard}>
            
            <Box className={styles.cardHeader}>
                <Box className={styles.cardHeaderLeft}>
                    <Box className={styles.cardIconWrap}>
                        {showEffectiveFromDate
                            ? <ScheduleOutlinedIcon className={styles.cardIcon} />
                            : <AutoAwesomeOutlinedIcon className={styles.cardIcon} />
                        }
                    </Box>

                    <Typography className={styles.sectionLabel}>
                        {title}
                    </Typography>
                </Box>

                {showEffectiveFromDate && effectiveFromDate ? (
                    <Typography className={styles.effectiveFromChip}>
                        Effective from {effectiveFromDate}
                    </Typography>
                ) : showPlanStartDate && planStartDate ? (
                    <Typography className={styles.effectiveFromChip}>
                        Started on {planStartDate}
                    </Typography>
                ) : null}
            </Box>

            <Box>
                <Box className={styles.planRow}>
                    <Typography className={styles.planName}>
                        {planName}
                    </Typography>

                    <Box className={styles.planPriceWrap}>
                        <Typography className={styles.planSummaryAmount}>
                            {currencySymbol || currencyCode}{thousandSeparator(planPrice)}
                        </Typography>

                        <Typography className={styles.billedLabel}>
                            Billed {getBillingCycleName(billingCycle)}
                        </Typography>
                    </Box>
                </Box>
            </Box>


            {plan && shouldShowAddOns(plan) && addOns.length > 0 && (
                <>
                    <Divider className={styles.divider} />

                    <Box className={styles.addOnsBlock}>
                        <Typography className={styles.sectionLabel}>
                            {addOnsLabel}
                        </Typography>

                        {addOns.map((addOn, index) => (
                            <Box
                                className={styles.addOnRow}
                                key={`${addOn.billing_add_on_id}-${index}`}
                            >
                                <Typography className={styles.addOnName}>
                                    {getAddOnTitle(addOn)} {getCreditsLabel(addOn)}
                                </Typography>

                                <Typography className={styles.addOnPrice}>
                                    {currencySymbol}
                                    {thousandSeparator(getAddOnAmount(addOn))}

                                    {isSubscriptionOneTimeAddOn(plan, addOn) && (
                                        <span className={styles.addOnPriceSuffix}>
                                            {" "}one-time
                                        </span>
                                    )}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </>
            )}
            
        </Box>
    );
};

export default PlanSummaryCard;
